import {
  findSpecialPricePeriodsToDelete,
  SpecialPricePeriodApiRequirements,
  SpecialPricePeriodChange,
  SpecialPricePeriodEditorContext,
  specialPricePeriodEditorMachine,
} from "./SpecialPricePeriodEditorMachine";
import { useActor, useSelector } from "@xstate/react";
import { classNames } from "@greeter/util";
import { DeleteButton } from "@greeter-venue/components/EditItemToolbar";
import {
  SpecialOpeningHours,
  SpecialPricePeriod,
  WeeklyOpeningHours,
} from "@greeter/core";
import {
  DatePeriodInput,
  LoadingOverlay,
  setupValidateOpeningsHoursMatch,
} from "@greeter/matter";
import { LenientDatePeriod } from "@greeter/date";
import css from "./SpecialPricePeriodEditor.module.scss";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import { SummaryTable } from "@greeter-venue/components/SummaryTable";
import * as uuid from "uuid";
import { HollowButton } from "@greeter-venue/components/HollowButton";
import { FloatingSaveButton } from "@greeter-venue/components/FloatingSaveButton";
import { FailedSection } from "./FailedSection";
import { arrowBackOutline } from "ionicons/icons";
import { PriceInput } from "@greeter/matter-v2";

const dateFormatter = new Intl.DateTimeFormat("da", {
  dateStyle: "short",
  timeStyle: "short",
});

const priceFormatter = new Intl.NumberFormat("da-DK");

function SpecialPricePeriodItem(props: {
  openingHours: WeeklyOpeningHours;
  specialOpeningHours: Array<SpecialOpeningHours>;
  specialPricePeriod: SpecialPricePeriod;
  lowest: Date;
  highest: Date;
  onChange: (specialPricePeriod: SpecialPricePeriod) => void;
  onDelete?: () => void;
}) {
  return (
    <div className={css.SpecialPricePeriodItem}>
      <div className={css.DatePeriodAndPrice}>
        <DatePeriodInput
          period={props.specialPricePeriod.period}
          validators={[
            setupValidateOpeningsHoursMatch({
              lowest: props.lowest,
              highest: props.highest,
              openingHours: props.openingHours,
              specialOpeningHours: props.specialOpeningHours,
              onMatchFrom(date) {
                props.onChange?.({
                  ...props.specialPricePeriod,
                  period: new LenientDatePeriod({
                    from: date,
                    to: props.specialPricePeriod.period.to,
                  }),
                });
              },
              onMatchTo(date) {
                props.onChange?.({
                  ...props.specialPricePeriod,
                  period: new LenientDatePeriod({
                    from: props.specialPricePeriod.period.from,
                    to: date,
                  }),
                });
              },
            }),
          ]}
          onChange={(dp) => {
            try {
              props.onChange({
                ...props.specialPricePeriod,
                period: new LenientDatePeriod(dp),
              });

              console.debug(
                "Change dateperiod to input",
                props.specialPricePeriod.price
              );
            } catch {
              console.warn("The input is invalid. Period was not updated.", dp);
            }
          }}
        />
        <PriceInput
          value={props.specialPricePeriod.price}
          onChange={(value) => {
            if (value) {
              props.onChange({
                ...props.specialPricePeriod,
                price: value,
              });
            }
          }}
        />
      </div>
      <DeleteButton onClick={props.onDelete} />
    </div>
  );
}

/**
 * A summation of the changes that has happened in the editor
 */
export type SpecialPricePeriodChangeset = {
  toSave: Array<SpecialPricePeriod>;
  toDelete: Array<string>;
};

export type SpecialPricePeriodEditorProps = {
  productId: string;
  api: SpecialPricePeriodApiRequirements;
  mode: SpecialPricePeriodEditorContext["mode"];
  onChange?: (changes: SpecialPricePeriodChangeset) => void;
};
export function SpecialPricePeriodEditor(props: SpecialPricePeriodEditorProps) {
  const [snap, send, m] = useActor(specialPricePeriodEditorMachine, {
    input: { productId: props.productId, mode: props.mode, api: props.api },
  });

  function change(c: SpecialPricePeriodChange) {
    send({ type: "change", data: c });
  }

  const changesExists = useSelector(
    m,
    (m) => m.context.changes.changes.length > 0
  );

  const specialPricePeriods = useSelector(m, (m) =>
    Array.from(m.context.changes.value.values())
  );
  const changeset: SpecialPricePeriodChangeset = useSelector(m, (m) => {
    return {
      toSave: Array.from(m.context.changes.value.values()),
      toDelete: findSpecialPricePeriodsToDelete(m.context.changes.changes),
    };
  });

  const [alert] = useIonAlert();

  let highest: Date | undefined;
  let lowest: Date | undefined;
  for (let i = 0, l = specialPricePeriods.length; i < l; i++) {
    const specialPricePeriod = specialPricePeriods[i];
    if (!lowest || specialPricePeriod.period.from < lowest)
      lowest = specialPricePeriod.period.from;
    if (!lowest || specialPricePeriod.period.to < lowest)
      lowest = specialPricePeriod.period.to;
    if (!highest || specialPricePeriod.period.from > highest)
      highest = specialPricePeriod.period.from;
    if (!highest || specialPricePeriod.period.to > highest)
      highest = specialPricePeriod.period.to;
  }

  const specialPricePeriodsRef = useSelector(m, (s) =>
    s.context.specialPricePeriodRef.getSnapshot()
  );
  const venueRef = useSelector(m, (s) => s.context.venueRef.getSnapshot());
  const specialOpeningHoursRef = useSelector(m, (s) =>
    s.context.specialOpeningHoursRef.getSnapshot()
  );

  return (
    <>
      {specialPricePeriodsRef.matches("fetching") && (
        <div style={{ height: 100, width: "100%", position: "relative" }}>
          <LoadingOverlay />
        </div>
      )}
      {specialPricePeriodsRef.matches("failed") && (
        <FailedSection
          reason="Kunne ikke hente specielle priser"
          onClick={() => {
            send({ type: "fetch", data: { productId: props.productId } });
          }}
        />
      )}
      {specialPricePeriodsRef.matches("idle") &&
        specialPricePeriods.length > 0 && (
          <SummaryTable
            headers={["Fra", "Til", "Pris"]}
            rows={specialPricePeriods.map((spp) => [
              dateFormatter.format(spp.period.from),
              dateFormatter.format(spp.period.to),
              priceFormatter.format(spp.price) + " kr.",
            ])}
            onClick={() => send({ type: "open" })}
          />
        )}
      {specialPricePeriodsRef.matches("idle") && (
        <HollowButton
          data-testid="open-special-price-period"
          onClick={() => send({ type: "open" })}
        >
          + Tilføj ny speciel prisperiode
        </HollowButton>
      )}
      <IonModal
        isOpen={snap.matches("opened")}
        onWillDismiss={() => {
          send({ type: "close" });
        }}
        style={{ "--background": "var(--gm-color-light-primary)" }}
      >
        <IonHeader style={{ "--background": "var(--gm-color-light-primary)" }}>
          <IonToolbar
            style={{
              "--background": "var(--gm-color-light-primary)",
              "--color": "var(--gm-color-dark-primary)",
            }}
          >
            <IonButtons slot="start">
              <IonButton
                onClick={() => {
                  if (changesExists) {
                    alert({
                      mode: "ios",
                      message:
                        "Hvis du går tilbage uden at gemme vil ændringerne gå tabt",
                      buttons: [
                        {
                          text: "Gem",
                          handler() {
                            if (props.onChange) props.onChange(changeset);
                            send({ type: "close" });
                          },
                        },
                        {
                          text: "Fortsæt",
                          handler() {
                            send({ type: "close" });
                            send({ type: "reset" });
                          },
                        },
                      ],
                    });
                  } else {
                    send({ type: "close" });
                  }
                }}
              >
                <IonIcon slot="icon-only" icon={arrowBackOutline} />
              </IonButton>
            </IonButtons>
            <IonTitle>Specielle prisperioder</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ "--background": "var(--gm-color-light-primary)" }}>
          <div className={css.SpecialPricePeriodEditor}>
            <div />
            {specialPricePeriods.map((spp) => {
              const exists = specialPricePeriods?.find(
                (_spp) => _spp.id === spp.id
              );
              return (
                <div
                  key={spp.id}
                  {...classNames(css.SpecialPricePeriodItems, !exists && "new")}
                >
                  <SpecialPricePeriodItem
                    specialOpeningHours={
                      specialOpeningHoursRef.context.data ?? []
                    }
                    openingHours={
                      venueRef.context.data?.bookableOpeningHours ??
                      new WeeklyOpeningHours()
                    }
                    lowest={lowest ?? new Date()}
                    highest={highest ?? new Date()}
                    specialPricePeriod={spp}
                    onChange={(newSpp) => change({ type: "set", data: newSpp })}
                    onDelete={() => change({ type: "delete", data: spp.id })}
                  />
                </div>
              );
            })}

            <HollowButton
              data-testid="new-special-price-period"
              onClick={() => change({ type: "add", data: { id: uuid.v7() } })}
            >
              + Tilføj ny speciel prisperiode
            </HollowButton>
          </div>

          <FloatingSaveButton
            data-testid="save-special-price-periods"
            show={changesExists}
            onClick={() => {
              if (props.onChange) {
                props.onChange(changeset);
              }
              send({ type: "close" });
            }}
          />
        </IonContent>
      </IonModal>
    </>
  );
}

export type { SpecialPricePeriodApiRequirements };
